// ─────────────────────────────────────────────────────────────────────────────
// CRM 2.0 — BulkActionBar
//
// Reusable bulk-action toolbar for the Lead, Deal, and Task list pages. When one
// or more rows are selected it lets the user pick a target value for that
// entity's op field (Lead→status, Deal→stage, Task→status) and submit it to
// `POST /api/v2/bulk/:entity`. The per-record updated/skipped results are then
// shown in a dialog (Requirements 8.1, 8.2, 8.4).
//
// This component owns only the action UI + submission; the parent page owns row
// selection (via `useRowSelection`) and passes the selected ids in. On success
// the underlying `useBulkAction` hook invalidates the entity's list query so the
// updated rows refresh in place.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useBulkAction } from '../hooks/useBulkAction';
import { BULK_ENTITY_NOUN, BULK_OP_FIELD_LABEL, BULK_OP_OPTIONS } from '../constants';
import type { BulkEntity, BulkResponse } from '../types';
import { BulkResultDialog } from './BulkResultDialog';

export interface BulkActionBarProps {
  /** Which entity the selected rows belong to. */
  entity: BulkEntity;
  /** The ids of the currently selected rows. */
  selectedIds: string[];
  /** Clears the parent's selection (called after a successful submit). */
  onClearSelection: () => void;
  /** Optional extra classes for layout tuning by the host page. */
  className?: string;
}

export function BulkActionBar({
  entity,
  selectedIds,
  onClearSelection,
  className,
}: BulkActionBarProps) {
  const options = BULK_OP_OPTIONS[entity];
  const fieldLabel = BULK_OP_FIELD_LABEL[entity];
  const noun = BULK_ENTITY_NOUN[entity];

  const [value, setValue] = useState('');
  const [result, setResult] = useState<BulkResponse | null>(null);
  const [resultOpen, setResultOpen] = useState(false);

  const bulkAction = useBulkAction(entity);

  const count = selectedIds.length;

  // Reset the chosen target whenever the selection is emptied so the bar starts
  // clean the next time rows are selected. Tracked during render (via the
  // previous count) instead of in an effect to avoid cascading renders.
  const [prevCount, setPrevCount] = useState(count);
  if (count !== prevCount) {
    setPrevCount(count);
    if (count === 0) setValue('');
  }

  if (count === 0) return null;

  const handleApply = () => {
    if (!value) {
      toast.error(`Pick a ${fieldLabel} to apply.`);
      return;
    }
    bulkAction.mutate(
      { ids: selectedIds, value },
      {
        onSuccess: (res) => {
          const updated = res.results.filter((r) => r.status === 'updated').length;
          const skipped = res.results.length - updated;
          setResult(res);
          setResultOpen(true);
          if (updated > 0 && skipped === 0) {
            toast.success(`Updated ${updated} ${noun}${updated === 1 ? '' : 's'}.`);
          } else if (updated > 0) {
            toast(`Updated ${updated}, skipped ${skipped}.`, { icon: '⚠️' });
          } else {
            toast(`No ${noun}s updated — ${skipped} skipped.`, { icon: '⚠️' });
          }
          // Clear the selection; successfully updated rows refresh via query
          // invalidation in the hook.
          onClearSelection();
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Bulk update failed.')),
      },
    );
  };

  return (
    <>
      <div
        className={
          'flex flex-wrap items-center gap-3 rounded border border-primary/30 bg-primary-container/40 px-4 py-2.5 ' +
          (className ?? '')
        }
        role="region"
        aria-label="Bulk actions"
      >
        <span className="text-data-md font-medium text-on-surface">
          {count} {noun}
          {count === 1 ? '' : 's'} selected
        </span>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-body-sm text-on-surface-variant">Set {fieldLabel}</span>
          <div className="w-48">
            <Select
              name={`bulk-${entity}-value`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              options={options}
              placeholder={`Select ${fieldLabel}…`}
              aria-label={`Target ${fieldLabel} for selected ${noun}s`}
            />
          </div>
          <Button size="sm" onClick={handleApply} loading={bulkAction.isPending} disabled={!value}>
            Apply to {count}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            aria-label="Clear selection"
            title="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <BulkResultDialog
        open={resultOpen}
        onClose={() => setResultOpen(false)}
        result={result}
        entityNoun={noun}
      />
    </>
  );
}
